import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { NEWSROOM_RETURN, newsroomDetailState } from "@/lib/newsroomNavigation";
import { api } from "@/lib/api";
import type { PageResponse } from "@/lib/pagination";
import { buildListQuery } from "@/lib/pagination";
import type { TodayItem, NewsItem, CommunityPost } from "@/lib/types";
import Pagination from "@/components/base/Pagination";
import ScrollReveal from "@/components/base/ScrollReveal";
import { useServerPagination } from "@/lib/useServerPagination";
import RichTextEditor from "@/components/feature/RichTextEditor";
import { normalizeNewsroomDate } from "@/lib/dateFormat";
import { deriveImageUrlForStorage, getPostThumbnailUrl, normalizeEditorHtmlForStorage } from "@/lib/html";
import { isValidYouTubeUrl, normalizeYouTubePosition } from "@/lib/youtube";
import type { YouTubePosition } from "@/lib/types";

type FormMode = "list" | "create" | "edit";
type MainCategory = "Biocon Today" | "Biocon News" | "Community";
const mainCategories: MainCategory[] = ["Biocon Today", "Biocon News", "Community"];

type AdminNewsroomRow = {
  type: "today" | "news" | "community";
  id: number;
  title: string;
  date: string;
  author?: string | null;
  imageUrl?: string | null;
  link?: string | null;
  youtubeUrl?: string | null;
  youtubePosition?: YouTubePosition | null;
  content?: string | null;
  excerpt?: string | null;
  category?: string | null;
  sortOrder?: number;
  status: string;
  views?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type UnifiedPost =
  | ({ _type: "today" } & TodayItem)
  | ({ _type: "news" } & NewsItem)
  | ({ _type: "community" } & CommunityPost);

function rowToUnified(row: AdminNewsroomRow): UnifiedPost {
  const base = {
    id: row.id,
    title: row.title,
    date: row.date,
    imageUrl: row.imageUrl ?? null,
    link: row.link ?? null,
    youtubeUrl: row.youtubeUrl ?? null,
    youtubePosition: normalizeYouTubePosition(row.youtubePosition),
    content: row.content ?? null,
    sortOrder: row.sortOrder ?? 0,
    status: row.status,
    createdAt: row.createdAt ?? undefined,
    updatedAt: row.updatedAt ?? undefined,
  };
  if (row.type === "today") return { _type: "today", ...base };
  if (row.type === "news") {
    return { _type: "news", ...base, author: row.author ?? "BIOCON", excerpt: row.excerpt ?? null };
  }
  return {
    _type: "community",
    ...base,
    author: row.author ?? "BIOCON",
    category: row.category ?? "General",
    views: row.views ?? 0,
  };
}

type PostForm = {
  mainCategory: MainCategory;
  communityCategory: string;
  title: string;
  date: string;
  author: string;
  link: string;
  youtubeUrl: string;
  youtubePosition: YouTubePosition;
  sortOrder: string;
  status: string;
  excerpt: string;
  content: string;
  views: string;
};

const defaultForm: PostForm = {
  mainCategory: "Biocon Today",
  communityCategory: "General",
  title: "", date: "", author: "BIOCON",
  link: "", youtubeUrl: "", youtubePosition: "top",
  sortOrder: "0", status: "active",
  excerpt: "", content: "", views: "0",
};

const inputCls = "w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20 bg-white";
const inputDisabledCls = "w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-100 text-gray-400 cursor-not-allowed opacity-80";

function typeToMainCategory(type: UnifiedPost["_type"]): MainCategory {
  if (type === "today") return "Biocon Today";
  if (type === "news") return "Biocon News";
  return "Community";
}

function postToMainCategory(post: UnifiedPost): MainCategory {
  return typeToMainCategory(post._type);
}

function postToForm(post: UnifiedPost): PostForm {
  const base: PostForm = {
    mainCategory: postToMainCategory(post),
    title: post.title,
    date: post.date,
    author: ("author" in post ? post.author : "") || "BIOCON",
    link: post.link || "",
    youtubeUrl: post.youtubeUrl || "",
    youtubePosition: normalizeYouTubePosition(post.youtubePosition),
    sortOrder: String(post.sortOrder ?? 0),
    status: post.status,
    excerpt: "",
    content: "",
    views: "0",
  };
  if (post._type === "today") base.content = post.content || "";
  if (post._type === "news") { base.excerpt = post.excerpt || ""; base.content = post.content || ""; }
  if (post._type === "community") {
    base.content = post.content || "";
    base.views = String(post.views ?? 0);
    base.communityCategory = post.category || "General";
  }
  return base;
}

async function deletePostByType(type: UnifiedPost["_type"], id: number) {
  if (type === "today") await api.delete(`/admin/news/today/${id}`);
  else if (type === "news") await api.delete(`/admin/news/news/${id}`);
  else await api.delete(`/admin/news/community/${id}`);
}

function CategoryBadge({ category }: { category: MainCategory }) {
  const map: Record<MainCategory, string> = {
    "Biocon Today": "bg-emerald-50 text-emerald-700",
    "Biocon News": "bg-sky-50 text-sky-700",
    "Community": "bg-amber-50 text-amber-700",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${map[category]}`}>
      {category}
    </span>
  );
}

export default function AdminNewsroomPage() {
  const [mode, setMode] = useState<FormMode>("list");
  const [form, setForm] = useState<PostForm>({ ...defaultForm });
  const [editId, setEditId] = useState<number | null>(null);
  const [editType, setEditType] = useState<UnifiedPost["_type"] | null>(null);
  const [filterCategory, setFilterCategory] = useState<"All" | MainCategory>("All");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const list = useServerPagination<UnifiedPost>({
    resetDeps: [search, filterCategory],
    enabled: mode === "list",
    fetchPage: useCallback(
      async (page, size) => {
        const res = await api.getPage<PageResponse<AdminNewsroomRow>>(
          "/admin/newsroom" +
            buildListQuery(page + 1, size, {
              search: search || undefined,
              category: filterCategory,
            })
        );
        const mapped = (res.data.content ?? []).map(rowToUnified);
        return { ...res.data, content: mapped };
      },
      [search, filterCategory]
    ),
  });

  const resetForm = () => {
    setForm({ ...defaultForm });
    setEditId(null);
    setEditType(null);
    setMode("list");
  };

  const handleMainCategoryChange = (cat: MainCategory) => {
    setForm((f) => {
      if (f.mainCategory === cat) return f;
      const next = { ...f, mainCategory: cat };
      if (cat !== "Biocon News") next.excerpt = "";
      if (cat !== "Community") next.views = "0";
      if (cat === "Community" && !next.communityCategory) next.communityCategory = "General";
      return next;
    });
  };

  const applyDateFormat = () => {
    const normalized = normalizeNewsroomDate(form.date);
    if (normalized) setForm((f) => ({ ...f, date: normalized }));
    return normalized;
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date.trim()) { showToast("제목과 날짜는 필수입니다."); return; }
    const youtubeUrl = form.youtubeUrl.trim();
    if (youtubeUrl && !isValidYouTubeUrl(youtubeUrl)) {
      showToast("YouTube URL 형식을 확인해주세요.");
      return;
    }
    const normalizedDate = normalizeNewsroomDate(form.date);
    if (!normalizedDate) {
      showToast("날짜 형식을 확인해주세요. (예: 20250518 또는 2025.05.18)");
      return;
    }
    setSaving(true);
    const imageUrl = deriveImageUrlForStorage(null, form.content);
    const content = normalizeEditorHtmlForStorage(form.content);
    const base = {
      title: form.title.trim(), date: normalizedDate,
      imageUrl,
      link: form.link.trim() || null,
      youtubeUrl: youtubeUrl || null,
      youtubePosition: form.youtubePosition,
      sortOrder: parseInt(form.sortOrder || "0", 10), status: form.status,
    };

    const saveToCategory = async (category: MainCategory, id: number | null) => {
      if (category === "Biocon Today") {
        const payload = { ...base, content };
        if (id) await api.put(`/admin/news/today/${id}`, payload);
        else await api.post("/admin/news/today", payload);
      } else if (category === "Biocon News") {
        const payload = { ...base, author: form.author || "BIOCON", excerpt: form.excerpt || null, content };
        if (id) await api.put(`/admin/news/news/${id}`, payload);
        else await api.post("/admin/news/news", payload);
      } else {
        const payload = {
          ...base,
          author: form.author || "BIOCON",
          content,
          category: form.communityCategory || "General",
        };
        if (id) await api.put(`/admin/news/community/${id}`, payload);
        else await api.post("/admin/news/community", payload);
      }
    };

    try {
      if (mode === "edit" && editId && editType) {
        const originalCategory = typeToMainCategory(editType);
        if (originalCategory !== form.mainCategory) {
          const confirmed = window.confirm(
            `카테고리를 "${originalCategory}"에서 "${form.mainCategory}"(으)로 변경합니다.\n기존 게시물은 삭제되고 새 카테고리에 등록됩니다. 계속하시겠습니까?`
          );
          if (!confirmed) {
            setSaving(false);
            return;
          }
          await saveToCategory(form.mainCategory, null);
          await deletePostByType(editType, editId);
        } else {
          await saveToCategory(form.mainCategory, editId);
        }
      } else {
        await saveToCategory(form.mainCategory, null);
      }
      showToast(mode === "edit" ? "수정되었습니다." : "등록되었습니다.");
      resetForm();
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "저장 실패");
    }
    setSaving(false);
  };

  const openEdit = (post: UnifiedPost) => {
    setEditId(post.id);
    setEditType(post._type);
    setForm(postToForm(post));
    setMode("edit");
  };

  const handleDelete = async (post: UnifiedPost) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      if (post._type === "today") await api.delete(`/admin/news/today/${post.id}`);
      else if (post._type === "news") await api.delete(`/admin/news/news/${post.id}`);
      else await api.delete(`/admin/news/community/${post.id}`);
      showToast("삭제되었습니다.");
      if (editId === post.id && editType === post._type) resetForm();
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "삭제 실패");
    }
  };

  return (
    <>
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 bg-[#2c4964] text-white text-sm rounded-full shadow-lg">
          {toast}
        </div>
      )}

      <div>
        {mode === "list" && (
          <>
            <ScrollReveal>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search" placeholder="Search posts..." value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20 w-full sm:w-64"
                    />
                  </div>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value as "All" | MainCategory)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white"
                  >
                    <option value="All">All Categories</option>
                    {mainCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => { setMode("create"); setForm({ ...defaultForm }); setEditId(null); setEditType(null); }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" /> Add Post
                </button>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.05}>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f1f7fc]">
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-16">ID</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-20">Image</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Category</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Title</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Author</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Date</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-16">Sort</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Status</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-40">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.loading ? (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
                    ) : list.items.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No posts found.</td></tr>
                    ) : (
                      list.items.map((post) => {
                        const mainCat = postToMainCategory(post);
                        const author = ("author" in post ? post.author : null) || "BIOCON";
                        return (
                          <tr key={`${post._type}-${post.id}`} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 border-b border-gray-100 text-gray-500">{post.id}</td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <img
                                src={getPostThumbnailUrl(post.imageUrl, post.content)}
                                alt="" className="w-12 h-9 rounded object-cover bg-gray-100"
                              />
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100"><CategoryBadge category={mainCat} /></td>
                            <td className="px-4 py-3 border-b border-gray-100 text-gray-700 font-medium max-w-xs truncate">{post.title}</td>
                            <td className="px-4 py-3 border-b border-gray-100 text-gray-500 text-xs whitespace-nowrap">{author}</td>
                            <td className="px-4 py-3 border-b border-gray-100 text-gray-500 text-xs whitespace-nowrap">{post.date}</td>
                            <td className="px-4 py-3 border-b border-gray-100 text-gray-500 text-xs">{post.sortOrder ?? 0}</td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                post.status === "active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"
                              }`}>{post.status}</span>
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <div className="flex items-center gap-2">
                                <Link
                                  to={`/newsroom/${post._type}/${post.id}`}
                                  state={newsroomDetailState(NEWSROOM_RETURN.adminNewsroom)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                                >
                                  <i className="ri-eye-line" /> View
                                </Link>
                                <button onClick={() => openEdit(post)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[#1977cc] hover:bg-[#f1f7fc] rounded transition-colors cursor-pointer">
                                  <i className="ri-edit-line" /> Edit
                                </button>
                                <button onClick={() => handleDelete(post)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer">
                                  <i className="ri-delete-bin-line" /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                {!list.loading && (
                  <Pagination
                    page={list.page}
                    totalPages={list.totalPages}
                    totalItems={list.totalItems}
                    pageSize={list.pageSize}
                    rangeStart={list.rangeStart}
                    rangeEnd={list.rangeEnd}
                    onPageChange={list.setPage}
                    onPageSizeChange={list.setPageSize}
                    pageSizeOptions={list.pageSizeOptions}
                  />
                )}
              </div>
            </ScrollReveal>
          </>
        )}

        {(mode === "create" || mode === "edit") && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="max-w-4xl mx-auto bg-white rounded-xl border border-gray-200 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#2c4964]">
                  {mode === "create" ? "Add Post" : "Edit Post"}
                </h2>
                <button onClick={resetForm}
                  className="text-sm text-gray-500 hover:text-[#1977cc] transition-colors cursor-pointer">Cancel</button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-[#2c4964] mb-2">Category <span className="text-red-500">*</span></label>
                <div className="flex items-center gap-2 flex-wrap">
                  {mainCategories.map((cat) => {
                    const isActive = form.mainCategory === cat;
                    const colorMap: Record<MainCategory, string> = {
                      "Biocon Today": "border-emerald-400 bg-emerald-50 text-emerald-700",
                      "Biocon News": "border-sky-400 bg-sky-50 text-sky-700",
                      "Community": "border-amber-400 bg-amber-50 text-amber-700",
                    };
                    const iconMap: Record<MainCategory, string> = {
                      "Biocon Today": "ri-camera-line",
                      "Biocon News": "ri-newspaper-line",
                      "Community": "ri-community-line",
                    };
                    return (
                      <button key={cat} type="button"
                        onClick={() => handleMainCategoryChange(cat)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          isActive ? colorMap[cat] : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                        }`}>
                        <i className={iconMap[cat]} />
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Title <span className="text-red-500">*</span></label>
                  <input type="text" value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className={inputCls} placeholder="제목을 입력하세요" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Date <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={form.date}
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                      onBlur={applyDateFormat}
                      className={inputCls}
                      placeholder="예: 20250518 또는 2025.05.18"
                    />
                  </div>

                  {form.mainCategory !== "Biocon Today" && (
                    <div>
                      <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Author</label>
                      <input type="text" value={form.author}
                        onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                        className={inputCls} />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Link URL</label>
                    <input type="text" value={form.link}
                      onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
                      className={inputCls} placeholder="https://..." />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">YouTube URL</label>
                    <input type="text" value={form.youtubeUrl}
                      onChange={(e) => setForm((f) => ({ ...f, youtubeUrl: e.target.value }))}
                      className={inputCls} placeholder="https://www.youtube.com/watch?v=..." />
                    {form.youtubeUrl.trim() && !isValidYouTubeUrl(form.youtubeUrl) && (
                      <p className="mt-1 text-xs text-red-500">유효한 YouTube URL을 입력해주세요.</p>
                    )}
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-1.5 ${
                        form.youtubeUrl.trim() ? "text-[#2c4964]" : "text-gray-400"
                      }`}
                    >
                      YouTube 표시 위치
                    </label>
                    <select
                      value={form.youtubePosition}
                      onChange={(e) => setForm((f) => ({ ...f, youtubePosition: e.target.value as YouTubePosition }))}
                      disabled={!form.youtubeUrl.trim()}
                      className={form.youtubeUrl.trim() ? `${inputCls} cursor-pointer` : inputDisabledCls}
                    >
                      <option value="top">상단 (본문 위)</option>
                      <option value="bottom">하단 (본문 아래)</option>
                    </select>
                    {!form.youtubeUrl.trim() && (
                      <p className="mt-1 text-xs text-gray-400">YouTube URL을 입력하면 선택할 수 있습니다.</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Sort Order</label>
                    <input type="number" value={form.sortOrder}
                      onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                      className={inputCls} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Status</label>
                    <select value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      className={inputCls}>
                      <option value="active">active</option>
                      <option value="inactive">inactive</option>
                    </select>
                  </div>
                </div>

                {form.mainCategory === "Biocon News" && (
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Excerpt</label>
                    <textarea value={form.excerpt}
                      onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                      rows={2} className={`${inputCls} resize-none`} placeholder="기사 요약..." />
                  </div>
                )}

                <RichTextEditor
                  label="Content"
                  value={form.content}
                  onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                  placeholder="게시물 본문 내용을 입력하세요..."
                />

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button onClick={resetForm}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer">Cancel</button>
                  <button onClick={handleSave} disabled={saving}
                    className="px-5 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
                    {saving ? "Saving..." : mode === "create" ? "Create Post" : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
}
